import { isUrlBypassed, isUrlExcludedFromThrottle } from '../config/throttle.config';

// Internal storage
const apiCallTimestamps = new Map();
// Counters for diagnostics
const apiCallCounts = new Map(); // successful attempts recorded
const apiThrottleCounts = new Map(); // number of times a call was throttled
let throttleSeconds = 30; // default cooldown in seconds

export const setApiCooldownSeconds = (seconds) => {
  throttleSeconds = Math.max(0, Number(seconds) || 0);
};

export const clearApiCallRecords = () => {
  apiCallTimestamps.clear();
  apiCallCounts.clear();
  apiThrottleCounts.clear();
};

export const logApiCallTotals = () => {
  const keys = new Set([...apiCallCounts.keys(), ...apiThrottleCounts.keys()]);
  const summary = {
    uniqueKeys: keys.size,
    totalCalls: 0,
    totalThrottles: 0,
    perKey: {},
  };

  for (const key of keys) {
    const calls = apiCallCounts.get(key) || 0;
    const throttles = apiThrottleCounts.get(key) || 0;
    summary.perKey[key] = { calls, throttles };
    summary.totalCalls += calls;
    summary.totalThrottles += throttles;
  }
  return summary;
};

export const getThrottleCount = (key) => apiThrottleCounts.get(key) || 0;

export const getTotalThrottleCount = () => {
  let sum = 0;
  for (const v of apiThrottleCounts.values()) sum += v;
  return sum;
};

const makeCallKey = (config) => {
  const method = (config.method || 'GET').toUpperCase();
  const url = config.url || '';
  let params = '';
  let data = '';

  try {
    params = config.params ? JSON.stringify(config.params) : '';
  } catch (e) {
    params = '[unserializable_params]';
  }

  try {
    data = config.data ? JSON.stringify(config.data) : '';
  } catch (e) {
    data = '[unserializable_data]';
  }

  return `${method}|${url}|${params}|${data}`;
};

export function checkAndRecordApiCall(config) {
  // BYPASS: If URL is in bypass list, skip ALL throttle/debounce logic entirely
  if (isUrlBypassed(config.url))
    return { key: makeCallKey(config), now: Date.now(), bypassed: true };

  // If URL is excluded from throttle (e.g., search endpoints), skip throttle enforcement
  if (isUrlExcludedFromThrottle(config.url))
    return { key: makeCallKey(config), now: Date.now(), skipped: true };

  const key = makeCallKey(config);
  const now = Date.now();
  const last = apiCallTimestamps.get(key);

  if (last && now - last < throttleSeconds * 1000) {
    apiThrottleCounts.set(key, (apiThrottleCounts.get(key) || 0) + 1);

    const wait = Math.ceil((throttleSeconds * 1000 - (now - last)) / 1000);
    const err = new Error(
      `API_THROTTLED: identical request made within ${throttleSeconds}s; wait ${wait}s`,
    );
    err.code = 'API_THROTTLED';
    err.waitSeconds = wait;
    err.key = key;
    throw err;
  }

  apiCallTimestamps.set(key, now);
  apiCallCounts.set(key, (apiCallCounts.get(key) || 0) + 1);

  setTimeout(
    () => {
      if (apiCallTimestamps.get(key) === now) apiCallTimestamps.delete(key);
    },
    throttleSeconds * 1000 + 1000,
  );

  return { key, now };
}
